// frontend/src/pages/Admin/FacilitatorsManagement.jsx
// ✅ CRUD COMPLETO DE FACILITADORES

import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  Table,
  Badge,
  Button,
  Input,
  Row,
  Col,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Alert,
} from 'reactstrap';
import api from '../../services/api';
import { parseDate } from '../../utils/dates';

// ✅ Opciones de especialidad (deben coincidir con el backend)
const SPECIALTY_OPTIONS = [
  { value: 'coaching', label: 'Coaching' },
  { value: 'training', label: 'Capacitación' },
  { value: 'consulting', label: 'Consultoría' },
  { value: 'workshop', label: 'Talleres' },
  { value: 'mentoring', label: 'Mentoría' },
  { value: 'other', label: 'Otro' },
];

// ✅ Opciones de estado
const STATUS_OPTIONS = [
  { value: 'active', label: 'Activo' },
  { value: 'inactive', label: 'Inactivo' },
];

function FacilitatorsManagement() {
  const [facilitators, setFacilitators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingFacilitator, setEditingFacilitator] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    specialty: 'coaching',  // ✅ Valor por defecto válido
    bio: '',
    status: 'active',  // ✅ Cambiado de is_active a status
  });
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadFacilitators();
  }, []);

  const loadFacilitators = async () => {
    try {
      setLoading(true);
      const response = await api.get('/facilitators/');
      console.log('✅ Facilitadores cargados:', response.data);
      setFacilitators(response.data.results || response.data);
    } catch (error) {
      console.error('Error cargando facilitadores:', error);
      alert('Error al cargar facilitadores');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (facilitator = null) => {
    if (facilitator) {
      setEditingFacilitator(facilitator);
      setFormData({
        name: facilitator.name || '',
        email: facilitator.email || '',
        phone: facilitator.phone || '',
        specialty: facilitator.specialty || 'coaching',
        bio: facilitator.bio || '',
        status: facilitator.status || 'active',  // ✅ Usar status
      });
    } else {
      setEditingFacilitator(null);
      setFormData({
        name: '',
        email: '',
        phone: '',
        specialty: 'coaching',
        bio: '',
        status: 'active',
      });
    }
    setError(null);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingFacilitator(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      specialty: 'coaching',
      bio: '',
      status: 'active',
    });
    setError(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!formData.name || !formData.email) {
      setError('Nombre y email son obligatorios');
      return;
    }

    setSubmitting(true);

    try {
      // ✅ Preparar datos para enviar (sin is_active)
      const dataToSend = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        specialty: formData.specialty,
        bio: formData.bio || null,
        status: formData.status,
      };

      console.log('📤 Enviando datos:', dataToSend);

      if (editingFacilitator) {
        await api.put(`/facilitators/${editingFacilitator.id}/`, dataToSend);
        await loadFacilitators();
        alert('Facilitador actualizado exitosamente');
      } else {
        await api.post('/facilitators/', dataToSend);
        await loadFacilitators();
        alert('Facilitador creado exitosamente');
      }
      handleCloseModal();
    } catch (error) {
      console.error('Error guardando facilitador:', error);
      const errorMsg = error.response?.data?.email?.[0] 
        || error.response?.data?.error 
        || error.response?.data?.detail
        || 'Error al guardar facilitador';
      setError(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (facilitator) => {
    try {
      const newStatus = facilitator.status === 'active' ? 'inactive' : 'active';
      await api.patch(`/facilitators/${facilitator.id}/`, {
        status: newStatus
      });
      await loadFacilitators();
      alert(`Facilitador ${newStatus === 'active' ? 'activado' : 'desactivado'} exitosamente`);
    } catch (error) {
      console.error('Error actualizando estado:', error);
      alert('Error al actualizar estado');
    }
  };

  const handleDelete = async (facilitator) => {
    if (!window.confirm(`¿Estás seguro de eliminar a ${facilitator.name}?`)) return;

    try {
      await api.delete(`/facilitators/${facilitator.id}/`);
      alert('Facilitador eliminado exitosamente');
      loadFacilitators();
    } catch (error) {
      console.error('Error eliminando facilitador:', error);
      alert('Error al eliminar facilitador');
    }
  };

  // ✅ Filtro corregido
  const filteredFacilitators = facilitators.filter(f => {
    const searchLower = searchTerm.toLowerCase();
    return (
      (f.name && f.name.toLowerCase().includes(searchLower)) ||
      (f.email && f.email.toLowerCase().includes(searchLower)) ||
      (f.specialty_display && f.specialty_display.toLowerCase().includes(searchLower)) ||
      (f.phone && f.phone.toLowerCase().includes(searchLower))
    );
  });

  // ✅ Contadores usando status
  const activeFacilitators = facilitators.filter(f => f.status === 'active').length;
  const inactiveFacilitators = facilitators.filter(f => f.status === 'inactive').length;

  // ✅ Helper para obtener el label de especialidad
  const getSpecialtyLabel = (value) => {
    const option = SPECIALTY_OPTIONS.find(opt => opt.value === value);
    return option ? option.label : value;
  };

  if (loading) {
    return (
      <div className="container-fluid py-4">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p className="text-muted mt-3">Cargando facilitadores...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      {/* Header con estadísticas */}
      <Row className="mb-4">
        <Col lg="12">
          <Card className="shadow border-0">
            <CardBody className="p-3">
              <Row className="align-items-center">
                <Col lg="8">
                  <div className="d-flex align-items-center">
                    <div className="icon icon-shape bg-gradient-primary shadow text-center rounded-circle me-3">
                      <i className="ni ni-hat-3 text-white text-lg opacity-10"></i>
                    </div>
                    <div>
                      <h5 className="mb-0">Gestión de Facilitadores</h5>
                      <p className="text-sm mb-0 text-secondary">
                        Administra los facilitadores que imparten servicios
                      </p>
                    </div>
                  </div>
                </Col>
                <Col lg="4" className="text-end">
                  <Badge color="success" pill className="badge-lg me-2">
                    <i className="ni ni-check-bold me-1"></i>
                    {activeFacilitators} activos
                  </Badge>
                  <Badge color="secondary" pill className="badge-lg">
                    <i className="ni ni-fat-remove me-1"></i>
                    {inactiveFacilitators} inactivos
                  </Badge>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Tabla de facilitadores */}
      <Row>
        <Col lg="12">
          <Card className="shadow border-0">
            <CardHeader className="bg-transparent border-0">
              <Row className="align-items-center">
                <Col lg="6">
                  <h6 className="mb-0">
                    <i className="ni ni-bullet-list-67 me-2"></i>
                    Facilitadores ({filteredFacilitators.length})
                  </h6>
                </Col>
                <Col lg="6">
                  <Row className="g-2">
                    <Col md="8">
                      <Input
                        type="text"
                        className="form-control-sm"
                        placeholder="🔍 Buscar facilitador..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </Col>
                    <Col md="4">
                      <Button
                        color="primary"
                        size="sm"
                        block
                        onClick={() => handleOpenModal()}
                      >
                        <i className="ni ni-fat-add me-1"></i>
                        Nuevo Facilitador
                      </Button>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </CardHeader>

            <CardBody className="px-0 pt-0 pb-2">
              <div className="table-responsive p-0">
                <Table className="align-items-center mb-0">
                  <thead>
                    <tr>
                      <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                        Facilitador
                      </th>
                      <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">
                        Email
                      </th>
                      <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">
                        Teléfono
                      </th>
                      <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                        Especialidad
                      </th>
                      
                      <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredFacilitators.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-center py-4">
                          <div className="py-4">
                            <i className="ni ni-hat-3 text-muted" style={{ fontSize: '3rem' }}></i>
                            <p className="text-secondary mb-0 mt-2">
                              {searchTerm 
                                ? 'No se encontraron facilitadores con ese criterio' 
                                : 'No hay facilitadores registrados'}
                            </p>
                            {!searchTerm && (
                              <Button
                                color="primary"
                                size="sm"
                                className="mt-3"
                                onClick={() => handleOpenModal()}
                              >
                                <i className="ni ni-fat-add me-1"></i>
                                Agregar Primer Facilitador
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ) : (
                      filteredFacilitators.map((facilitator) => (
                        <tr key={facilitator.id}>
                          <td>
                            <div className="d-flex px-3 py-1">
                              <div className="avatar avatar-sm bg-gradient-primary me-3 shadow">
                                <span className="text-white text-xs font-weight-bold">
                                  {facilitator.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                                </span>
                              </div>
                              <div className="d-flex flex-column justify-content-center">
                                <h6 className="mb-0 text-sm">{facilitator.name}</h6>
                                <p className="text-xs text-secondary mb-0">
                                  <i className="ni ni-calendar-grid-58 me-1"></i>
                                  Desde {parseDate(facilitator.created_at).toLocaleDateString('es-MX')}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td>
                            <p className="text-xs font-weight-bold mb-0">
                              <i className="ni ni-email-83 me-1"></i>
                              {facilitator.email}
                            </p>
                          </td>
                          <td>
                            <p className="text-xs font-weight-bold mb-0">
                              <i className="ni ni-mobile-button me-1"></i>
                              {facilitator.phone || 'N/A'}
                            </p>
                          </td>
                          <td className="align-middle text-center">
                            <Badge color="primary" className="badge-sm">
                              {facilitator.specialty_display || getSpecialtyLabel(facilitator.specialty)}
                            </Badge>
                          </td>
                          
                          <td className="align-middle text-center">
                        
                            <Button
                              color="primary"
                              size="sm"
                              className="me-2"
                              onClick={() => handleOpenModal(facilitator)}
                              title="Editar"
                            >
                              <i className=""></i>
                              Editar
                            </Button>
                            
                            <Button
                              color="danger"
                              size="sm"
                              onClick={() => handleDelete(facilitator)}
                              title="Eliminar"
                            >
                              <i className=""></i>
                              Eliminar
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Modal para agregar/editar */}
      <Modal isOpen={showModal} toggle={handleCloseModal} centered size="lg">
        <ModalHeader toggle={handleCloseModal}>
          <i className="ni ni-hat-3 me-2"></i>
          {editingFacilitator ? 'Editar Facilitador' : 'Nuevo Facilitador'}
        </ModalHeader>
        <form onSubmit={handleSubmit}>
          <ModalBody>
            {error && (
              <Alert color="danger">
                <span className="alert-icon"><i className="ni ni-support-16"></i></span>
                <span className="alert-text">{error}</span>
              </Alert>
            )}

            <Row>
              <Col md="6">
                <FormGroup>
                  <Label className="form-control-label">
                    Nombre Completo <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="text"
                    name="name"
                    className="form-control-alternative"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Ej: Juan Pérez García"
                    required
                  />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label className="form-control-label">
                    Email <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="email"
                    name="email"
                    className="form-control-alternative"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="facilitador@ejemplo.com"
                    required
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md="6">
                <FormGroup>
                  <Label className="form-control-label">
                    Teléfono
                  </Label>
                  <Input
                    type="tel"
                    name="phone"
                    className="form-control-alternative"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Ej: +52 123 456 7890"
                  />
                </FormGroup>
              </Col>
              <Col md="6">
                {/* ✅ CAMBIADO A SELECT */}
                <FormGroup>
                  <Label className="form-control-label">
                    Especialidad <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="select"
                    name="specialty"
                    className="form-control-alternative"
                    value={formData.specialty}
                    onChange={handleChange}
                    required
                  >
                    {SPECIALTY_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md="6">
                {/* ✅ CAMBIADO A SELECT PARA STATUS */}
                <FormGroup>
                  <Label className="form-control-label">
                    Estado
                  </Label>
                  <Input
                    type="select"
                    name="status"
                    className="form-control-alternative"
                    value={formData.status}
                    onChange={handleChange}
                  >
                    {STATUS_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Input>
                  <small className="text-muted">
                    Los facilitadores inactivos no aparecerán en los selectores
                  </small>
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label className="form-control-label">
                    Biografía
                  </Label>
                  <Input
                    type="textarea"
                    name="bio"
                    className="form-control-alternative"
                    value={formData.bio}
                    onChange={handleChange}
                    placeholder="Descripción breve del facilitador..."
                    rows="3"
                  />
                </FormGroup>
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={handleCloseModal}>
              <i className="ni ni-fat-remove me-1"></i>
              Cancelar
            </Button>
            <Button color="primary" type="submit" disabled={submitting}>
              {submitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Guardando...
                </>
              ) : (
                <>
                  <i className="ni ni-check-bold me-1"></i>
                  {editingFacilitator ? 'Actualizar' : 'Crear'}
                </>
              )}
            </Button>
          </ModalFooter>
        </form>
      </Modal>
    </div>
  );
}

export default FacilitatorsManagement;
